// ─────────────────────────────────────────────────────────────────────────────
// Shared referral-payout engine
//
// Turns "this client was referred by an employee" into per-employee dollars for
// a given month. Single source of truth for the Referral Leaderboard so the
// board, the drill-down, and anything that later pays these out cannot drift.
//
// ── The two rules that shape everything here ────────────────────────────────
//
// 1. THE TAG IS ON THE CLIENT, NOT THE JOB. Every job that client books counts,
//    including ones booked before the tag was added. So attribution is a join
//    (referral_clients → jb_jobs), not a stored per-job row.
//
// 2. A REFERRAL EARNS IN THE MONTH THE JOB IS BILLED, AND ROLLS FORWARD UNTIL
//    THEN. An open job is not skipped and not paid — it shows every month as
//    carried-forward with a projection, then lands in whichever month it is
//    actually invoiced. Dylan 2026-07-28: "if the job is active it should still
//    show but keeps pushing to the next month if it's not closed out/billed in
//    that month."
//
// ⚠️ GST: jb_jobs.total_revenue and quoted_total are tax-IN. Paying a % of them
// raw overpays by 5%, so revenue is divided by 1.05 before the rate is applied.
// jb_jobs.profit is already a cost-subtracted figure and is NOT re-adjusted.
// ─────────────────────────────────────────────────────────────────────────────

export const GST_DIVISOR = 1.05;

export const PAYOUT_BASES = [
  { value: "revenue_pct", label: "% of revenue", suffix: "%",
    hint: "Percent of the job's revenue, GST stripped." },
  { value: "profit_pct", label: "% of profit", suffix: "%",
    hint: "Percent of the job's profit after labour + materials." },
  { value: "flat_amount", label: "Flat $ per job", suffix: "$",
    hint: "Fixed dollars per job, whatever the job is worth." },
];

// Used ONLY when the settings row cannot be read. The component surfaces that
// as an error rather than quietly showing numbers computed from this — a tech
// and the owner must never see different dollars for the same month.
export const FALLBACK_RULE = { basis: "revenue_pct", rate: 2 };

// ⚠️ "archived" is NOT dead. It is Jobber's normal terminal state for finished
// work — 1118 of 1182 jb_jobs rows carry it, 1064 of those with revenue. Listing
// it here silently dropped every completed job from the board (caught 2026-07-28
// when a client with 23 paid jobs credited only the 1 still open). "deleted" is
// the only status that means the job did not happen.
const DEAD_STATUSES = ["deleted"];

export function monthKeyOf(date) {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function monthEnd(monthKey) {
  const [y, m] = String(monthKey).split("-").map(Number);
  return new Date(y, m, 0, 23, 59, 59, 999);
}

export function shiftMonth(monthKey, delta) {
  const [y, m] = String(monthKey).split("-").map(Number);
  return monthKeyOf(new Date(y, m - 1 + delta, 1));
}

export function monthLabel(monthKey) {
  const [y, m] = String(monthKey).split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-CA", { month: "long", year: "numeric" });
}

// jb_jobs.closed_date arrives as Jobber's "Apr 02, 2026" string on some rows and
// an ISO date on others. Parsing "2026-04-02" with `new Date` yields UTC
// midnight, which reads as March 31 in Pacific — the same trap CrewPerformance
// documents at :115. Build plain dates at LOCAL midnight instead.
export function parseJobDate(v) {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  const s = String(v).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

export function exGst(amount) {
  const n = Number(amount) || 0;
  return n / GST_DIVISOR;
}

// What one job pays out in total, before it is split between referrers.
// Returns null when the rule cannot be applied (no revenue yet) so callers can
// tell "earns nothing" apart from "not knowable yet".
export function jobPayoutAmount(job, rule) {
  const basis = rule?.basis || FALLBACK_RULE.basis;
  const rate = Number(rule?.rate);
  if (!isFinite(rate)) return null;

  if (basis === "flat_amount") return rate;

  if (basis === "profit_pct") {
    const profit = Number(job.profit);
    if (!isFinite(profit)) return null;
    // A job that lost money pays no referral rather than a negative one.
    return Math.max(0, profit) * (rate / 100);
  }

  const revenue = revenueExGstOf(job);
  if (revenue == null) return null;
  return revenue * (rate / 100);
}

// Realized ex-GST revenue. total_revenue is the invoice-maintained figure;
// amount_invoiced is the same number from the rollup and covers rows the
// revenue refresh has not reached yet.
export function revenueExGstOf(job) {
  const raw = Number(job.total_revenue) || Number(job.amount_invoiced) || 0;
  if (!raw) return null;
  return exGst(raw);
}

// Best guess at what an unbilled job will pay, so a carried-forward row shows
// something useful. Quoted totals are tax-in like invoiced ones.
export function projectedPayoutOf(job, rule) {
  const realized = jobPayoutAmount(job, rule);
  if (realized != null) return { amount: realized, projected: false };

  const basis = rule?.basis || FALLBACK_RULE.basis;
  const rate = Number(rule?.rate);
  if (!isFinite(rate)) return { amount: null, projected: true };
  if (basis === "flat_amount") return { amount: rate, projected: false };

  const quoted = Number(job.quoted_total) || 0;
  if (!quoted) return { amount: null, projected: true };
  if (basis === "profit_pct") {
    // No profit figure yet — no defensible projection off a quote total alone.
    return { amount: null, projected: true };
  }
  return { amount: exGst(quoted) * (rate / 100), projected: true };
}

// The month a job's money actually landed. First invoice wins; a job closed
// with revenue but no cached invoice row falls back to its closed date.
export function billedMonthOf(job, invoiceDates) {
  const first = (invoiceDates || [])
    .map(parseJobDate)
    .filter(Boolean)
    .sort((a, b) => a - b)[0];
  if (first) return monthKeyOf(first);
  if (revenueExGstOf(job) != null) {
    const closed = parseJobDate(job.closed_date);
    if (closed) return monthKeyOf(closed);
  }
  return null;
}

export function isDeadJob(job) {
  const s = String(job.status || "").toLowerCase();
  const d = String(job.derived_status || "").toLowerCase();
  return DEAD_STATUSES.includes(s) || DEAD_STATUSES.includes(d);
}

/**
 * Join referral tags to jobs and produce one row per (job × referrer).
 *
 * @param referralClients rows from referral_clients (only `matched` ones pay)
 * @param jobs            jb_jobs rows
 * @param invoices        invoice rows used only to DATE the payout. Pass both
 *                        jobber_invoices ({ job_number, issued_date }) and
 *                        jb_invoices ({ job_nums, issued_date, created_at,
 *                        due_date }) — see the note on invoiceDatesByJob below
 *                        for why neither table alone is enough.
 * @param rule            { basis, rate }
 * @param overrides       rows from referral_payout_overrides — per-(job ×
 *                        referrer) sidecar. custom_amount REPLACES the computed
 *                        payout (it is the employee's final dollars, after any
 *                        share split); paid/paid_month/paid_amount are the
 *                        Send-to-Cindy stamps that stop a payout re-carrying.
 */
export function buildReferralRows({ referralClients = [], jobs = [], invoices = [], rule, overrides = [] }) {
  const overrideByKey = new Map();
  for (const ov of overrides) {
    const k = `${String(ov.job_num ?? "")}|${String(ov.employee_name || "").trim().toLowerCase()}`;
    overrideByKey.set(k, ov);
  }
  // Invoice dates per job number — used to date the payout.
  //
  // ⚠️ TWO traps here, both of which silently pushed money into the wrong month
  // (found 2026-09-10 chasing Rick Priegnitz #2684 off Taylor's August board):
  //
  // 1. jb_invoices.job_number is NULL on every one of its 1426 rows. The job
  //    link lives in job_nums — TEXT, comma-separated when one invoice covers
  //    two jobs ("2574,2562"). Reading job_number alone left this map EMPTY, so
  //    billedMonthOf() fell through to closed_date for EVERY job the engine has
  //    ever priced. #2684 billed Aug 21 but closed Sep 1, so it dated to
  //    September.
  // 2. jb_invoices.issued_date is the INGEST stamp, not Jobber's issued date —
  //    it tracks created_at to within ~20 seconds. An invoice picked up a day
  //    late is stamped a day late, and on a month boundary that moves money:
  //    #1843 was issued Aug 31 and stored as Sep 1, pushing job 2736 into
  //    September. jobber_invoices carries the TRUE issuedDate.
  //
  // So callers pass BOTH tables and the earliest date wins. That is safe in the
  // union: an ingest stamp can never precede the issue it records, so the true
  // date wins wherever jobber_invoices has the row, and jb_invoices still
  // covers the rows it is missing.
  const invoiceDatesByJob = new Map();
  for (const inv of invoices) {
    const raw = inv.job_nums ?? inv.job_number;
    if (raw == null) continue;
    const date = inv.issued_date || inv.created_at || inv.due_date;
    if (!date) continue;
    for (const jn of String(raw).split(",").map(s => s.trim()).filter(Boolean)) {
      if (!invoiceDatesByJob.has(jn)) invoiceDatesByJob.set(jn, []);
      invoiceDatesByJob.get(jn).push(date);
    }
  }

  // Tags are keyed by Jobber client id, but jb_jobs rows predating the client-id
  // backfill only carry client_name — so index both and prefer the id.
  const byClientId = new Map();
  const byClientName = new Map();
  for (const rc of referralClients) {
    if (!rc.matched || !(rc.linked_employees || []).length) continue;
    if (rc.jobber_client_id) byClientId.set(String(rc.jobber_client_id), rc);
    const nm = String(rc.client_name || "").trim().toLowerCase();
    if (nm && !byClientName.has(nm)) byClientName.set(nm, rc);
  }

  const rows = [];
  for (const job of jobs) {
    if (isDeadJob(job)) continue;
    const tag = byClientId.get(String(job.client_id || "")) ||
                byClientName.get(String(job.client_name || "").trim().toLowerCase());
    if (!tag) continue;

    const jobNum = String(job.job_num ?? "");
    const billedMonth = billedMonthOf(job, invoiceDatesByJob.get(jobNum));
    const { amount, projected } = projectedPayoutOf(job, rule);
    // A job only exists from the month it was created — it cannot carry forward
    // into months before that.
    const startedMonth = monthKeyOf(parseJobDate(job.created_date || job.created_at));

    for (const emp of tag.linked_employees) {
      const share = Number(emp.share_pct);
      const pct = isFinite(share) && share > 0 ? share : 100;
      const ov = overrideByKey.get(`${jobNum}|${String(emp.name || "").trim().toLowerCase()}`);
      const hasCustom = ov != null && ov.custom_amount != null && isFinite(Number(ov.custom_amount));
      rows.push({
        employee: emp.name,
        sharePct: pct,
        jobNum,
        jobTitle: job.title || null,
        clientName: job.client_name || tag.client_name || null,
        referralText: tag.referral_text || null,
        manual: !!tag.manual_override,
        revenueExGst: revenueExGstOf(job),
        profit: Number(job.profit) || null,
        // A custom amount is the admin's final word — no share split, no
        // projection flag (an explicitly set dollar is definite even if the
        // job hasn't billed; WHEN it pays is still bill-month driven).
        amount: hasCustom ? Number(ov.custom_amount) : (amount == null ? null : (amount * pct) / 100),
        computedAmount: amount == null ? null : (amount * pct) / 100,
        overridden: hasCustom,
        overrideNote: ov?.note || null,
        paid: !!ov?.paid,
        paidMonth: ov?.paid_month ? monthKeyOf(parseJobDate(ov.paid_month)) : null,
        paidAmount: ov?.paid_amount != null ? Number(ov.paid_amount) : null,
        projected: hasCustom ? false : projected,
        billedMonth,
        startedMonth,
        invoiceStatus: job.invoice_status || null,
        derivedStatus: job.derived_status || job.status || null,
      });
    }
  }
  return rows;
}

/**
 * Split the rows for one month into what was earned and what rolls forward.
 *
 * earned  — the job was billed in THIS month; this is the payable number.
 * carried — the job existed by the end of this month and still had not billed,
 *           so it pushes into the next month. Shown, never counted as payable.
 */
export function splitMonth(rows, monthKey, programStart = null) {
  // Jobs billed before the programme started are history, not payable.
  const floor = programStart ? monthKeyOf(parseJobDate(programStart)) : null;
  const earned = [];
  const carried = [];
  for (const r of rows) {
    if (floor && r.billedMonth && r.billedMonth < floor) continue;
    if (r.billedMonth === monthKey) { earned.push(r); continue; }
    const notYetBilled = !r.billedMonth || r.billedMonth > monthKey;
    const existedBy = !r.startedMonth || r.startedMonth <= monthKey;
    if (notYetBilled && existedBy) carried.push(r);
  }
  return { earned, carried };
}

// Per-employee totals for one month, ranked by earned dollars.
export function leaderboardForMonth(rows, monthKey, programStart = null) {
  const { earned, carried } = splitMonth(rows, monthKey, programStart);
  const map = new Map();
  const bucket = (name) => {
    if (!map.has(name)) {
      map.set(name, {
        tech: name,
        earned: 0, earnedJobs: 0,
        carried: 0, carriedJobs: 0,
        carriedUnknown: 0,
        earnedRows: [], carriedRows: [],
      });
    }
    return map.get(name);
  };

  for (const r of earned) {
    const b = bucket(r.employee);
    b.earned += r.amount || 0;
    b.earnedJobs += 1;
    b.earnedRows.push(r);
  }
  for (const r of carried) {
    const b = bucket(r.employee);
    if (r.amount == null) b.carriedUnknown += 1;
    else b.carried += r.amount;
    b.carriedJobs += 1;
    b.carriedRows.push(r);
  }

  return [...map.values()].sort((a, b) =>
    (b.earned - a.earned) || (b.carried - a.carried) || a.tech.localeCompare(b.tech)
  );
}

// Months that have any referral activity, newest first, always including the
// current month so a fresh board is never empty.
export function activeMonths(rows, currentMonthKey) {
  const keys = new Set([currentMonthKey]);
  for (const r of rows) {
    if (r.billedMonth) keys.add(r.billedMonth);
    if (r.startedMonth) keys.add(r.startedMonth);
  }
  return [...keys].filter(Boolean).sort().reverse();
}
